/**
 * Table of contents component.
 *
 * @param {HTMLElement} tocElement
 */
export function TableOfContents(tocElement) {
  const content = document.querySelector('.layout__content');
  const items = tocElement.querySelectorAll('.toc__item');
  const links = tocElement.querySelectorAll('.toc__link');
  const sections = content.querySelectorAll('h2[id]');
  const toggle = tocElement.querySelector('.toc__toggle');
  const isMobile = window.matchMedia('only screen and (max-width: 992px)').matches; // single check on load
  const linkClicked = new Event('toc-link-clicked');
  let activeTocItem = null;

  /**
   * Scroll down to a specific section of the page.
   * Specifically to the heading title of that section.
   * - Offset adjusts the placement of that scrolled position to keep the heading title in view
   *
   * @param {HTMLElement} element The element to scroll to.
   */
  const scrollToElement = (element) => {
    const { top } = document.body.getBoundingClientRect();
    const offset = isMobile ? tocElement.getBoundingClientRect().height + 40 : 20;
    const elTop = element.getBoundingClientRect().top;
    const position = Math.round(elTop - offset - top);
    window.scrollTo(0, position);
  };

  /**
   * Update Aria Label on TOC toggle button
   *
   * @param {HTMLButtonElement} button the button with the aria label that needs to be adjusted
   */
  const updateTocToggleAriaLabel = (button) => {
    if (!button) return; // bail if no button exists
    if (button.classList.contains('toc__toggle--active')) {
      button.setAttribute('aria-label', 'close');
    } else {
      button.setAttribute('aria-label', 'open');
    }
  };

  /**
   * Close the table of contents.
   */
  const closeToc = () => {
    toggle.classList.remove('toc__toggle--active');
    tocElement.classList.remove('toc--active');
    updateTocToggleAriaLabel(toggle);
  };

  /**
   * Add active class to an active TOC item
   */
  const activateTocItem = () => {
    // if we have an active TOC item, add the active class
    if (activeTocItem && !isMobile) {
      activeTocItem.classList.add('toc__item--active');
    }
  };

  /**
   * Handles clicking a specific table of content item.
   *
   * @param e
   */
  const onLinkClick = (e) => {
    const element = e.currentTarget.closest('.toc__item');
    const link = element.querySelector('.toc__link');
    const targetId = link.getAttribute('href').split('#')[1];
    const targetElement = document.getElementById(targetId);

    // Dispatch event to let other components know that a TOC link was clicked.
    document.dispatchEvent(linkClicked);

    // Scroll to target.
    scrollToElement(targetElement);

    // grab active TOC element clicked on
    activeTocItem = element;

    // Close table of contents.
    closeToc();
  };

  /**
   * Handles toggling the table of contents popout on mobile.
   *
   * @param e
   */
  const onToggleClick = (e) => {
    e.preventDefault();
    const element = e.currentTarget;

    element.classList.toggle('toc__toggle--active');
    tocElement.classList.toggle('toc--active');
    updateTocToggleAriaLabel(toggle);
  };

  /**
   * Handles on page scroll events.
   *
   * - When scrolling past a given section, the active element in the table of contents will be
   *   highlighted.
   * - Offset handles the sensitivity of how close the scrolled section,
   *   before changing highlighted TOC item
   */
  const onScroll = () => {
    const scrollTop = Math.round(window.scrollY);
    const offset = isMobile ? Math.round(tocElement.getBoundingClientRect().height + 80) : 80;
    items.forEach((item) => {
      item.classList.remove('toc__item--active');
    });

    for (let i = sections.length - 1; i >= 0; i--) {
      if (scrollTop > sections[i].getBoundingClientRect().top + scrollTop - offset || i === 0) {
        const { id } = sections[i];
        activeTocItem = tocElement.querySelector(`.toc__link[href="#${id}"]`).parentNode;

        if (!isMobile) {
          activateTocItem();
        }
        break;
      }
    }
  };

  /**
   * on FAQ page, add additional resources to the table of contents.
   */
  const addAdditionalResourcesToToc = () => {
    const additionalResources = document.querySelector('.page-faq .resources');
    if (additionalResources) {
      const resourcesTitleEl = additionalResources.querySelector('.resources__title');
      const resourcesTitleId = (resourcesTitleEl) ? resourcesTitleEl.getAttribute('id') : null;
      const resourcesTitle = (resourcesTitleEl) ? resourcesTitleEl.textContent.trim() : null;

      if (resourcesTitleId && resourcesTitle) {
        const additionalResourcesItem = document.createElement('li');
        additionalResourcesItem.classList.add('toc__item');
        additionalResourcesItem.innerHTML = `
          <a class="toc__link" href="#${resourcesTitleId}">${resourcesTitle}</a>
        `;
        document.getElementById('toc__list').appendChild(additionalResourcesItem);
        additionalResourcesItem.addEventListener('click', closeToc);
      }
    }
  };

  /**
   * Initializes the event handlers.
   */
  const addEventListeners = () => {
    onScroll(); // handles case when a page is refreshed in the middle of the page
    document.addEventListener('scroll', onScroll);
    toggle.addEventListener('click', onToggleClick);

    links.forEach((link) => {
      link.addEventListener('click', (e) => e.preventDefault());
    });
    items.forEach((item) => {
      item.addEventListener('click', onLinkClick);
    });

    document.addEventListener('scrollend', activateTocItem);
  };

  /**
   * Initialize our component.
   */
  const init = () => {
    addAdditionalResourcesToToc();
    addEventListeners();
  };
  init();
}
